import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { CheckCircle2, ShieldCheck, Copy } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { createNotification } from "@/lib/notifications";
import { useI18n } from "@/lib/i18n";

export const Route = createFileRoute("/delete-data")({
  component: DeleteDataPage,
  head: () => ({
    meta: [
      { title: "Request Data Deletion — AdKavi" },
      {
        name: "description",
        content:
          "Request deletion of your personal data and any data AdKavi has received from connected ad platforms (Meta, Google, TikTok).",
      },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: "Request Data Deletion — AdKavi" },
      {
        property: "og:description",
        content:
          "Submit a request to delete your AdKavi account data and connected ad platform data.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://adkavi.com/delete-data" },
    ],
    links: [{ rel: "canonical", href: "https://adkavi.com/delete-data" }],
  }),
});

const schema = z.object({
  full_name: z.string().trim().min(1, "Name is required").max(100),
  email: z.string().trim().email("Please enter a valid email").max(255),
  reason: z.string().trim().max(1000).optional(),
});

function DeleteDataPage() {
  const { t } = useI18n();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [form, setForm] = useState({ full_name: "", email: "", reason: "" });
  // Honeypot field — bots fill this in, real users don't
  const [website, setWebsite] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot check — silently "succeed" so bots don't retry
    if (website.trim() !== "") {
      setSubmitted(true);
      return;
    }

    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? t("deleteData.toast.invalidInput"));
      return;
    }

    setSubmitting(true);

    // Rate-limit: max 3 submissions per email per 24h
    const { data: recent } = await supabase.rpc("recent_deletion_request_count", {
      _email: parsed.data.email,
    });
    if (typeof recent === "number" && recent >= 1) {
      setSubmitting(false);
      toast.error(t("deleteData.toast.alreadySubmitted"));
      return;
    }

    const { data: inserted, error } = await supabase
      .from("data_deletion_requests")
      .insert({
        full_name: parsed.data.full_name,
        email: parsed.data.email,
        reason: parsed.data.reason || null,
        user_agent:
          typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
      })
      .select("request_id")
      .single();

    if (error || !inserted) {
      setSubmitting(false);
      toast.error(t("deleteData.toast.submitFailed"));
      return;
    }

    // Fire-and-forget notification email; don't block UX on failure
    try {
      await supabase.functions.invoke("send-data-deletion-notification", {
        body: {
          full_name: parsed.data.full_name,
          email: parsed.data.email,
          reason: parsed.data.reason || null,
          request_id: inserted.request_id,
        },
      });
    } catch (e) {
      console.error("notification failed", e);
    }

    // In-app notification (no-op if user is not signed in)
    void createNotification({
      kind: "deletion_request",
      title: "Data deletion request submitted",
      body: `Request ${inserted.request_id} received. We'll email you with updates.`,
      link: "/delete-data/status",
      metadata: { request_id: inserted.request_id },
    });

    // Deletion is processed by an admin via the admin dashboard after
    // verifying the request. We intentionally do NOT auto-trigger the
    // destructive deletion endpoint from the client, since that endpoint
    // can permanently wipe any user account.


    setSubmitting(false);
    setRequestId(inserted.request_id);
    setSubmitted(true);
  };

  const copyId = async () => {
    if (!requestId) return;
    try {
      await navigator.clipboard.writeText(requestId);
      toast.success(t("deleteData.toast.idCopied"));
    } catch {
      toast.error(t("deleteData.toast.copyFailed"));
    }
  };

  return (
    <main className="min-h-screen bg-background px-6 py-16">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
          ← Back to home
        </Link>

        <div className="mt-6 flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-primary" />
          </div>
          <h1 className="text-3xl font-bold">Request Data Deletion</h1>
        </div>

        <p className="mt-4 text-muted-foreground">
          You can request deletion of your AdKavi account data and any data
          AdKavi has received from connected ad platforms (Meta, Google,
          TikTok). Submit the form below and we'll process your request within
          30 days, in line with applicable privacy laws (GDPR, CCPA) and Meta
          Platform Terms.
        </p>

        <p className="mt-2 text-sm text-muted-foreground">
          Already submitted?{" "}
          <Link to="/delete-data/status" search={{}} className="underline text-foreground">
            Check the status of your request
          </Link>
          .
        </p>

        {submitted ? (
          <div className="mt-8 rounded-2xl border border-border/50 bg-card/50 backdrop-blur p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-primary/15 mx-auto mb-4 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-xl font-semibold">Request received</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              We've recorded your data deletion request. Our team will email you
              at <span className="font-medium text-foreground">{form.email}</span>{" "}
              once the deletion is complete.
            </p>
            {requestId && (
              <div className="mt-6 inline-flex flex-col items-center gap-2">
                <span className="text-xs text-muted-foreground uppercase tracking-wide">
                  Your request ID
                </span>
                <button
                  type="button"
                  onClick={copyId}
                  className="font-mono text-base bg-muted/60 hover:bg-muted px-3 py-1.5 rounded-md inline-flex items-center gap-2"
                >
                  {requestId}
                  <Copy className="w-3.5 h-3.5" />
                </button>
                <p className="text-xs text-muted-foreground max-w-sm">
                  Save this ID. You can use it together with your email to{" "}
                  <Link
                    to="/delete-data/status"
                    search={{ email: form.email, id: requestId }}
                    className="underline"
                  >
                    check status
                  </Link>{" "}
                  at any time.
                </p>
              </div>
            )}
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="mt-8 space-y-5 rounded-2xl border border-border/50 bg-card/50 backdrop-blur p-6 sm:p-8"
          >
            {/* Honeypot — visually hidden, hidden from accessibility tree */}
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                left: "-10000px",
                width: "1px",
                height: "1px",
                overflow: "hidden",
              }}
            >
              <label htmlFor="website">Website</label>
              <input
                id="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="full_name">Full name</Label>
              <Input
                id="full_name"
                required
                maxLength={100}
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                placeholder="Jane Doe"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email address</Label>
              <Input
                id="email"
                type="email"
                required
                maxLength={255}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@example.com"
              />
              <p className="text-xs text-muted-foreground">
                Please use the same email address you used to create your AdKavi account.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reason">Additional details (optional)</Label>
              <Textarea
                id="reason"
                maxLength={1000}
                rows={4}
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="Anything else we should know about your request"
              />
            </div>

            <Button type="submit" disabled={submitting} className="w-full">
              {submitting ? "Submitting…" : "Submit deletion request"}
            </Button>

            <p className="text-xs text-muted-foreground">
              Prefer email? Contact{" "}
              <a href="mailto:support@adkavi.com" className="underline">
                support@adkavi.com
              </a>
              .
            </p>
          </form>
        )}
      </div>
    </main>
  );
}
